import styled from "styled-components";

import { itsMyOwnSegment } from "app/pages/segments/analysis/itsMyOwnSegment";
import { isLinesOfBusinessSegment } from "app/services/segments/LinesOfBusinessUtils";
import type { ICustomSegment, ISegmentBase } from "app/services/segments/segmentsApiService";
import { SEGMENT_TYPES } from "app/services/segments/segmentsApiService";
import { SwTrack } from "app/services/SwTrack";
import { allTrackers } from "app/services/track/track";
import { StyledItemIcon } from "pro-features/components/core cells/src/CoreRecentCell/StyledComponents";
import { CoreSegmentCell } from "pro-features/components/core cells/src/CoreSegmentCell/CoreSegmentCell";
import { CoreWebsiteCell } from "pro-features/components/core cells/src/CoreWebsiteCell/CoreWebsiteCell";
import { proNavigator } from "single-spa/react-app/ProNavigator";

// temporarily moved here from a deleted file - app/pages/companies/analysis/traffic/trafficAndEngagement/companiesEngagementTable/styledComponents.tsx
const CompanyCellContainer = styled.div`
    margin-top: -5px;
    ${StyledItemIcon} {
        width: 24px;
        height: 24px;
        flex-shrink: 0;
        border-color: #d6dbe1;
        display: flex;

        .ItemIcon-img {
            width: 18px;
            height: 18px;
        }
    }
`;

export enum EEntityType {
    WEBSITE = 1,
    SEGMENT = 2,
}

const getNavigationParams = () => {
    const { country, webSource, duration } = proNavigator.getParams();
    const navigationParams = {
        duration,
        webSource,
        country,
        isWWW: "*",
    };
    return navigationParams;
};

const getDomainInternalLink = (domain: string) => {
    const domainInternalLink = proNavigator.href("digitalsuite_website_websiteperformance", {
        ...getNavigationParams(),
        key: domain,
    });
    return domainInternalLink;
};

const getSegmentInternalLink = (segmentId: string) => {
    const segmentInternalLink = proNavigator.href("digitalsuite_segments-analysis-traffic", {
        ...getNavigationParams(),
        mode: "single",
        id: segmentId,
    });
    return segmentInternalLink;
};

export const getSegmentOrDomainCellEnrichedAttributes = (
    row: { Type: number; Id: string; Domain: string; DisplayName?: string },
    segment: ISegmentBase,
) => {
    if (!row) {
        return { SegmentType: SEGMENT_TYPES.WEBSITE };
    }

    const { Domain: domain, Id: id, DisplayName: displayName } = row;

    const domainInternalLink = getDomainInternalLink(domain);
    const defaultReturnValue = {
        SegmentType: SEGMENT_TYPES.WEBSITE,
        internalLink: domainInternalLink,
    };

    // Handle LOB segments - they don't have a segment object but have DisplayName
    if (!segment && isLinesOfBusinessSegment(id)) {
        return {
            SegmentType: SEGMENT_TYPES.SEGMENT,
            SegmentId: id,
            SegmentName: displayName,
            internalLink: domainInternalLink,
        };
    }

    if (!segment) {
        return defaultReturnValue;
    }

    const { Type: type, Id: SegmentId } = row;
    if (!type || !SegmentId) {
        return defaultReturnValue;
    }

    const isWebsite = type === EEntityType.WEBSITE;
    const internalLink = isWebsite ? domainInternalLink : getSegmentInternalLink(SegmentId);

    const segmentName = segment.segmentName;
    const favicon = segment.favicon;

    // Properties specific to ICustomSegment
    let lastUpdated: string | undefined;
    let userId: string | undefined;

    if ("lastUpdated" in segment && "userId" in segment) {
        lastUpdated = (segment as ICustomSegment).lastUpdated;
        userId = (segment as ICustomSegment).userId;
    } else {
        lastUpdated = undefined;
        userId = undefined;
    }

    return {
        SegmentType: isWebsite ? SEGMENT_TYPES.WEBSITE : SEGMENT_TYPES.SEGMENT,
        SegmentId,
        SegmentName: segmentName,
        SegmentLastUpdated: lastUpdated,
        userId,
        internalLink,
        favicon,
    };
};

export const SegmentOrDomainCellWithoutDomainSign = (props: FixmeType) => {
    return (
        <SegmentOrDomainCell
            {...props}
            withWebsiteSign={false}
            CoreWebsiteCellDomainBadge={null}
        />
    );
};

const SegmentOrDomainCell = ({
    value,
    row,
    withWebsiteSign = true,
    CoreWebsiteCellDomainBadge = "WEBSITE",
    dataAutomationAttribute = "segment-or-domain-table-cell",
}: FixmeType) => {
    const subtitleFilters = [];
    const editSegmentClick = () => {
        SwTrack.all.trackEvent("Internal Link", "click", `segmentCard/editSegment/${row.SegmentId}`);
        proNavigator.go("digitalsuite_segments-wizard", { sid: row.SegmentId });
    };
    if (row.SegmentType === SEGMENT_TYPES.SEGMENT) {
        subtitleFilters.push({
            filter: "text",
            value: row.SegmentName,
        });
    }

    return (
        <CompanyCellContainer data-automation={dataAutomationAttribute}>
            {row.SegmentType === SEGMENT_TYPES.WEBSITE ? (
                <CoreWebsiteCell
                    withWebsiteSign={withWebsiteSign}
                    icon={row.Favicon}
                    domain={value}
                    domainBadge={CoreWebsiteCellDomainBadge}
                    externalLink={`http://${value}`}
                    // @ts-ignore FIXME: strict migration - filter shape not assignable to ReactNode — CoreWebsiteCell forwards to BoxSubtitle which accepts Filter[]
                    subtitleFilters={subtitleFilters.length > 0 ? subtitleFilters : null}
                    trackExternalLink={() =>
                        allTrackers.trackEvent("external link", "click", "Company Trafic And Engagement Over Time")
                    }
                    hideTrackButton={true}
                    internalLink={row.internalLink}
                />
            ) : (
                <CoreSegmentCell
                    withWebsiteSign={true}
                    segmentName={row.SegmentName}
                    lastModified={row.SegmentLastUpdated}
                    isOrgSegment={!itsMyOwnSegment(row.userId)}
                    onClick={editSegmentClick}
                    segmentId={row.SegmentId}
                    icon={row.Favicon}
                    domain={value}
                    externalLink={`http://${value}`}
                    subtitleFilters={subtitleFilters.length > 0 ? subtitleFilters : null}
                    trackExternalLink={() =>
                        allTrackers.trackEvent("external link", "click", "Segments Group Analysis")
                    }
                    internalLink={row.internalLink}
                />
            )}
        </CompanyCellContainer>
    );
};
